import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactToggle from 'shared/design-system/components/TrueImpactToggle';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactConfirmationDialog from 'shared/design-system/components/TrueImpactConfirmationDialog';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faPlus,
  faTrash,
  faTriangleExclamation,
  faFlag,
  faEnvelope,
} from '@fortawesome/pro-light-svg-icons';
import {
  Feature,
  FeatureUserOverride,
  createFeature,
  deleteFeature,
  deleteFeatureUser,
  getAllFeatures,
  getFeatureUserOverrides,
  setFeatureUser,
  updateFeature,
} from './api';
import PlatformOverrides from './PlatformOverrides';

const NAME_HELPER =
  'Use camelCase letters only — no numbers, special characters, or spaces. The first letter is always lower case.';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Sanitize a name as the user types: letters only, force first char lower case.
const sanitizeName = (raw: string): string => {
  const lettersOnly = raw.replace(/[^A-Za-z]/g, '');
  if (!lettersOnly) return '';
  return lettersOnly.charAt(0).toLowerCase() + lettersOnly.slice(1);
};

const PageWrapper = styled.div`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: ${SPACERS.large};
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${SPACERS.medium};
  gap: ${SPACERS.small};
  .header-left {
    display: flex;
    align-items: center;
    gap: ${SPACERS.small};
    .header-icon {
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      background: ${COLORS.ctaPrimaryBackground};
      color: ${COLORS.ctaPrimary};
    }
    .heading {
      margin: 0;
    }
    .subheading {
      margin: 2px 0 0;
      color: ${COLORS.grayDark};
    }
  }
`;

const CreateCard = styled.div`
  background: ${COLORS.whiteBase};
  border: 1px solid ${COLORS.greyLight};
  border-radius: 12px;
  padding: ${SPACERS.medium};
  margin-bottom: ${SPACERS.medium};
  box-shadow: 0 2px 8px rgba(0, 115, 163, 0.05);
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${SPACERS.small};
    margin-top: ${SPACERS.small};
    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }
  .field-label {
    display: block;
    margin-bottom: 4px;
    color: ${COLORS.grayDarkest};
  }
  .helper {
    color: ${COLORS.grayDark};
    margin-top: 4px;
    display: block;
  }
  .helper.error {
    color: ${COLORS.critical};
  }
  .toggle-row {
    display: flex;
    align-items: center;
    gap: ${SPACERS.small};
    margin-top: ${SPACERS.small};
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: ${SPACERS.small};
    margin-top: ${SPACERS.medium};
  }
`;

const FlagCard = styled.div<{ disabled?: boolean }>`
  background: ${COLORS.whiteBase};
  border: 1px solid ${COLORS.greyLight};
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: ${SPACERS.small};
  box-shadow: 0 1px 4px rgba(0, 115, 163, 0.05);
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
  transition: box-shadow 0.2s, transform 0.2s;
  &:hover {
    box-shadow: 0 4px 14px rgba(0, 115, 163, 0.09);
  }
`;

const FlagHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  padding: ${SPACERS.small} ${SPACERS.medium};
  cursor: pointer;
  user-select: none;
  .flag-icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: ${COLORS.ctaBrandBackground};
    color: ${COLORS.ctaBrand};
    flex-shrink: 0;
  }
  .flag-id-name {
    flex: 1;
    min-width: 0;
    .flag-name {
      font-weight: 600;
      color: ${COLORS.blackBase};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .flag-desc {
      color: ${COLORS.grayDark};
      font-size: 13px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
  .status-pill.on {
    background: ${COLORS.successBackground};
    color: ${COLORS.success};
  }
  .status-pill.off {
    background: ${COLORS.grayLight};
    color: ${COLORS.grayDarkest};
  }
  .status-pill.disabled {
    background: ${COLORS.criticalBackground};
    color: ${COLORS.critical};
  }
  .chevron {
    color: ${COLORS.grayDark};
  }
`;

const FlagBody = styled.div`
  border-top: 1px solid ${COLORS.greyLight};
  padding: ${SPACERS.medium};
  background: ${COLORS.greyLightest};
  .row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${SPACERS.small};
    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }
  .field-label {
    display: block;
    margin-bottom: 4px;
    color: ${COLORS.grayDarkest};
  }
  .toggle-row {
    display: flex;
    align-items: center;
    gap: ${SPACERS.small};
    margin-top: ${SPACERS.small};
  }
  .actions {
    display: flex;
    gap: ${SPACERS.small};
    margin-top: ${SPACERS.medium};
    justify-content: flex-end;
    flex-wrap: wrap;
  }
`;

const OverridesSection = styled.div`
  margin-top: ${SPACERS.medium};
  background: ${COLORS.whiteBase};
  border: 1px solid ${COLORS.greyLight};
  border-radius: 10px;
  padding: ${SPACERS.small} ${SPACERS.medium};
  .section-title {
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
    margin-bottom: ${SPACERS.small};
    color: ${COLORS.grayDarkest};
    font-weight: 600;
  }
  .empty {
    color: ${COLORS.grayDark};
    font-size: 13px;
    padding: ${SPACERS.tiny} 0;
  }
  .override-list {
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.tiny};
  }
  .override-item {
    display: flex;
    align-items: center;
    gap: ${SPACERS.small};
    padding: ${SPACERS.tiny} ${SPACERS.small};
    border: 1px solid ${COLORS.greyLight};
    border-radius: 8px;
    background: ${COLORS.greyLightest};
    .email {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: ${COLORS.blackBase};
    }
    .delete-btn {
      cursor: pointer;
      color: ${COLORS.critical};
      background: transparent;
      border: none;
      padding: 4px;
      display: flex;
      align-items: center;
      &:hover {
        opacity: 0.7;
      }
    }
  }
  .add-override-row {
    display: flex;
    gap: ${SPACERS.small};
    align-items: center;
    margin-top: ${SPACERS.small};
    flex-wrap: wrap;
    .email-input {
      flex: 1;
      min-width: 220px;
      align-self: center;
    }
    .value-toggle {
      display: flex;
      align-items: center;
      gap: ${SPACERS.tiny};
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${SPACERS.extraLarge} ${SPACERS.large};
  color: ${COLORS.grayDark};
  background: ${COLORS.greyLightest};
  border-radius: 12px;
  border: 1px dashed ${COLORS.greyBase};
  .icon {
    font-size: 40px;
    color: ${COLORS.grayBase};
    margin-bottom: ${SPACERS.small};
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${SPACERS.medium};
`;

interface CreateFormProps {
  existingNames: string[];
  onCreate: (input: {
    name: string;
    description: string;
    value: boolean;
  }) => Promise<void>;
  isSaving: boolean;
}

const CreateFlagForm: React.FC<CreateFormProps> = ({
  existingNames,
  onCreate,
  isSaving,
}) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState(false);

  const trimmedName = name.trim();
  const isDuplicate =
    !!trimmedName && existingNames.some(n => n === trimmedName);
  const isValid = trimmedName.length >= 2 && !isDuplicate;

  const reset = () => {
    setName('');
    setDescription('');
    setValue(false);
  };

  if (!open) {
    return (
      <div style={{ marginBottom: SPACERS.medium }}>
        <TrueImpactButton
          variant="primary-small"
          onClick={() => setOpen(true)}
          className="add-flag-button"
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
          Add Feature Flag
        </TrueImpactButton>
      </div>
    );
  }

  return (
    <CreateCard>
      <TrueImpactText variant="heading-subheading">
        New feature flag
      </TrueImpactText>
      <div className="form-row">
        <div>
          <TrueImpactText className="field-label" variant="body-small-bold">
            Name
          </TrueImpactText>
          <TrueImpactTextfield
            placeholder="myFeatureFlag"
            value={name}
            onChange={v => setName(sanitizeName(v))}
            isError={isDuplicate}
          />
          <TrueImpactText
            variant="body-small"
            className={`helper${isDuplicate ? ' error' : ''}`}
          >
            {isDuplicate
              ? 'A flag with this name already exists.'
              : NAME_HELPER}
          </TrueImpactText>
        </div>
        <div>
          <TrueImpactText className="field-label" variant="body-small-bold">
            Description (optional)
          </TrueImpactText>
          <TrueImpactTextfield
            placeholder="What does this flag control?"
            value={description}
            onChange={v => setDescription(v)}
          />
        </div>
      </div>
      <div className="toggle-row">
        <TrueImpactText variant="body-small-bold">Default value</TrueImpactText>
        <TrueImpactToggle enabled={value} onClick={() => setValue(v => !v)} />
        <TrueImpactText
          variant="body-small"
          color={value ? 'success' : 'grayDark'}
        >
          {value ? 'On' : 'Off'}
        </TrueImpactText>
      </div>
      <div className="actions">
        <TrueImpactButton
          variant="secondary-small"
          onClick={() => {
            reset();
            setOpen(false);
          }}
          disabled={isSaving}
        >
          Cancel
        </TrueImpactButton>
        <TrueImpactButton
          variant="primary-small"
          disabled={!isValid || isSaving}
          onClick={async () => {
            await onCreate({ name: trimmedName, description, value });
            reset();
            setOpen(false);
          }}
        >
          {isSaving ? 'Creating…' : 'Create'}
        </TrueImpactButton>
      </div>
    </CreateCard>
  );
};

interface OverridesProps {
  feature: Feature;
}

const Overrides: React.FC<OverridesProps> = ({ feature }) => {
  const client = useClient();
  const [overrides, setOverrides] = useState<FeatureUserOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newValue, setNewValue] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const { data } = await getFeatureUserOverrides(client, feature.dbId);
      setOverrides(data?.featureUserOverrides || []);
    } finally {
      setLoading(false);
    }
  }, [client, feature.dbId]);

  useEffect(() => {
    load();
  }, [load]);

  const trimmedEmail = newEmail.trim().toLowerCase();
  const emailValid = EMAIL_REGEX.test(trimmedEmail);
  const emailError = emailTouched && !!trimmedEmail && !emailValid;

  const handleAdd = async () => {
    if (!client || !emailValid) return;
    setSaving(true);
    try {
      await setFeatureUser(client, {
        featureId: feature.dbId,
        email: trimmedEmail,
        value: newValue,
      });
      setNewEmail('');
      setNewValue(true);
      setEmailTouched(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!client) return;
    await deleteFeatureUser(client, id);
    await load();
  };

  return (
    <OverridesSection>
      <div className="section-title">
        <FontAwesomeIcon icon={faEnvelope} />
        <TrueImpactText variant="body-bold">Per-user overrides</TrueImpactText>
      </div>
      {loading ? (
        <LoadingWrapper>
          <TrueImpactLoading />
        </LoadingWrapper>
      ) : overrides.length === 0 ? (
        <div className="empty">
          No overrides yet. All users see the default value above.
        </div>
      ) : (
        <div className="override-list">
          {overrides.map(o => (
            <div key={o.dbId} className="override-item">
              <span className="email">{o.email}</span>
              <span
                className={`status-pill ${o.value ? 'on' : 'off'}`}
                style={{
                  background: o.value
                    ? COLORS.successBackground
                    : COLORS.grayLight,
                  color: o.value ? COLORS.success : COLORS.grayDarkest,
                  fontWeight: 700,
                  fontSize: 11,
                  padding: '3px 10px',
                  borderRadius: 12,
                  letterSpacing: 0.3,
                }}
              >
                {o.value ? 'On' : 'Off'}
              </span>
              <button
                type="button"
                className="delete-btn"
                title="Remove override"
                onClick={() => handleDelete(o.dbId)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="add-override-row">
        <div className="email-input">
          <TrueImpactTextfield
            placeholder="user@example.com"
            value={newEmail}
            onChange={v => {
              setNewEmail(v);
              setEmailTouched(true);
            }}
            onBlur={() => setEmailTouched(true)}
            isError={emailError}
          />
          {emailError && (
            <TrueImpactText
              variant="body-small"
              color="critical"
              style={{ marginTop: 4, display: 'block' }}
            >
              Enter a valid email address.
            </TrueImpactText>
          )}
        </div>
        <div className="value-toggle">
          <TrueImpactText variant="body-small">Value</TrueImpactText>
          <TrueImpactToggle
            enabled={newValue}
            onClick={() => setNewValue(v => !v)}
          />
          <TrueImpactText
            variant="body-small"
            color={newValue ? 'success' : 'grayDark'}
          >
            {newValue ? 'On' : 'Off'}
          </TrueImpactText>
        </div>
        <TrueImpactButton
          variant="primary-small"
          disabled={!emailValid || saving}
          onClick={handleAdd}
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
          {saving ? 'Adding…' : 'Add override'}
        </TrueImpactButton>
      </div>
    </OverridesSection>
  );
};

interface FlagItemProps {
  feature: Feature;
  expanded: boolean;
  onToggleExpand: () => void;
  refreshFeatures: () => void;
}

const FlagItem: React.FC<FlagItemProps> = ({
  feature,
  expanded,
  onToggleExpand,
  refreshFeatures,
}) => {
  const client = useClient();
  const [name, setName] = useState(feature.name);
  const [description, setDescription] = useState(feature.description || '');
  const [value, setValue] = useState(feature.value);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    setName(feature.name);
    setDescription(feature.description || '');
    setValue(feature.value);
  }, [feature.dbId, feature.name, feature.description, feature.value]);

  const isDirty =
    name !== feature.name ||
    (description || '') !== (feature.description || '') ||
    value !== feature.value;
  const isNameValid = name.trim().length >= 2;

  const handleSave = async () => {
    if (!client || !isNameValid || !isDirty) return;
    setSaving(true);
    try {
      await updateFeature(client, {
        id: feature.dbId,
        name,
        description: description || null,
        value,
      });
      refreshFeatures();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!client) return;
    setSaving(true);
    try {
      await deleteFeature(client, feature.dbId);
      setDeleteOpen(false);
      refreshFeatures();
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = !feature.enabled
    ? { label: 'Disabled', cls: 'disabled' as const }
    : feature.value
    ? { label: 'On', cls: 'on' as const }
    : { label: 'Off', cls: 'off' as const };

  return (
    <FlagCard disabled={!feature.enabled}>
      <FlagHeader onClick={onToggleExpand}>
        <div className="flag-icon">
          <FontAwesomeIcon icon={faFlag} />
        </div>
        <div className="flag-id-name">
          <div className="flag-name">{feature.name}</div>
          <div className="flag-desc">
            {feature.description || 'No description'}
          </div>
        </div>
        <span className={`status-pill ${statusLabel.cls}`}>
          {statusLabel.label}
        </span>
        <FontAwesomeIcon
          className="chevron"
          icon={expanded ? faChevronUp : faChevronDown}
        />
      </FlagHeader>
      <TrueImpactCollapse show={expanded}>
        <FlagBody>
          <div className="row">
            <div>
              <TrueImpactText className="field-label" variant="body-small-bold">
                Name
              </TrueImpactText>
              <TrueImpactTextfield
                placeholder="myFeatureFlag"
                value={name}
                onChange={v => setName(sanitizeName(v))}
                isError={!isNameValid}
              />
              <TrueImpactText
                variant="body-small"
                color="grayDark"
                style={{ marginTop: 4, display: 'block' }}
              >
                {NAME_HELPER}
              </TrueImpactText>
            </div>
            <div>
              <TrueImpactText className="field-label" variant="body-small-bold">
                Description
              </TrueImpactText>
              <TrueImpactTextfield
                placeholder="Optional description"
                value={description}
                onChange={v => setDescription(v)}
              />
            </div>
          </div>
          <div className="toggle-row">
            <TrueImpactText variant="body-small-bold">
              Default value
            </TrueImpactText>
            <TrueImpactToggle
              enabled={value}
              onClick={() => setValue(v => !v)}
            />
            <TrueImpactText
              variant="body-small"
              color={value ? 'success' : 'grayDark'}
            >
              {value ? 'On' : 'Off'}
            </TrueImpactText>
          </div>

          <Overrides feature={feature} />

          <PlatformOverrides feature={feature} />

          <div className="actions">
            <TrueImpactButton
              variant="secondary-small-error"
              onClick={() => setDeleteOpen(true)}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faTrash} style={{ marginRight: 6 }} />
              Delete flag
            </TrueImpactButton>
            <TrueImpactButton
              variant="primary-small"
              disabled={!isDirty || !isNameValid || saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </TrueImpactButton>
          </div>
        </FlagBody>
      </TrueImpactCollapse>

      <TrueImpactConfirmationDialog
        open={deleteOpen}
        icon={faTriangleExclamation}
        iconColor="critical"
        handleClose={() => setDeleteOpen(false)}
        previous={{
          text: 'Cancel',
          onClick: () => setDeleteOpen(false),
        }}
        next={{
          text: saving ? 'Deleting…' : 'Delete',
          onClick: handleDelete,
        }}
      >
        <TrueImpactText variant="heading-subheading">
          Delete flag?
        </TrueImpactText>
        <TrueImpactText style={{ marginTop: SPACERS.small, display: 'block' }}>
          The flag <b>{feature.name}</b> will be soft-deleted. It will no longer
          appear in <code>allFeatures</code> or in any user's
          <code> User.features</code> map. A superadmin can restore it later via{' '}
          <code>updateFeature(id, enabled: true)</code>.
        </TrueImpactText>
      </TrueImpactConfirmationDialog>
    </FlagCard>
  );
};

const FeatureFlags: React.FC = () => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest<{
    allFeatures: Feature[];
  }>();
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const refresh = useCallback(() => {
    if (!client) return;
    makeRequest(getAllFeatures(client));
  }, [client, makeRequest]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Preserve the last successful features array so cards (and their
  // Save/Delete buttons) stay mounted during background refetch.
  const lastFeaturesRef = useRef<Feature[]>([]);
  const features: Feature[] = useMemo(() => {
    if (requestState.type === 'REQUEST_SUCCESS') {
      lastFeaturesRef.current = requestState.data.allFeatures || [];
    }
    return lastFeaturesRef.current;
  }, [requestState]);

  const existingNames = useMemo(() => features.map(f => f.name), [features]);

  const handleCreate = useCallback(
    async (input: { name: string; description: string; value: boolean }) => {
      if (!client) return;
      setCreating(true);
      try {
        await createFeature(client, {
          name: input.name,
          description: input.description || null,
          value: input.value,
          enabled: true,
        });
        refresh();
      } finally {
        setCreating(false);
      }
    },
    [client, refresh],
  );

  const isLoading =
    requestState.type === 'REQUEST_START' ||
    requestState.type === 'REQUEST_INIT';

  return (
    <PageWrapper>
      <PageHeader>
        <div className="header-left">
          <div className="header-icon">
            <FontAwesomeIcon icon={faFlag} size="lg" />
          </div>
          <div>
            <TrueImpactText variant="heading-page" className="heading">
              Feature Flags
            </TrueImpactText>
            <TrueImpactText className="subheading">
              Manage feature flags and per-user overrides.
            </TrueImpactText>
          </div>
        </div>
      </PageHeader>

      <CreateFlagForm
        existingNames={existingNames}
        onCreate={handleCreate}
        isSaving={creating}
      />

      {isLoading && features.length === 0 ? (
        <LoadingWrapper>
          <TrueImpactLoading />
        </LoadingWrapper>
      ) : features.length === 0 ? (
        <EmptyState>
          <FontAwesomeIcon icon={faFlag} className="icon" />
          <TrueImpactText>
            No feature flags yet. Create your first one above.
          </TrueImpactText>
        </EmptyState>
      ) : (
        <div
          style={{
            opacity: isLoading ? 0.6 : 1,
            transition: 'opacity 0.2s',
          }}
        >
          {features.map(feature => (
            <FlagItem
              key={feature.dbId}
              feature={feature}
              expanded={expandedId === feature.dbId}
              onToggleExpand={() =>
                setExpandedId(prev =>
                  prev === feature.dbId ? null : feature.dbId,
                )
              }
              refreshFeatures={refresh}
            />
          ))}
        </div>
      )}
    </PageWrapper>
  );
};

export default FeatureFlags;
